// --- 공통 타입 정의 ---
export type VehicleModel = "현대 포터II 일렉트릭" | "기아 봉고III EV";
export type Depot = "동부산" | "인천" | "마포" | "서울" | "부산" | "대구";
export type VehicleStatus = "운행중" | "충전중" | "수리 입고" | "대기";
export type ObdStatus = "통신중" | "에러" | "-";
export type FleetOpsStatus = "Open" | "In progress" | "Resolved";
export type LicenseType = "1종 보통" | "1종 대형" | "1종 특수 (대형견인)" | "1종 특수 (소형견인)" | "특수 (구난차)" | "2종 보통" | "2종 소형";

// 차량 데이터 인터페이스
export interface Vehicle {
  vehicleNumber: string;
  vin: string;
  model: VehicleModel;
  assignee?: string;
  depot: Depot | "";
  status: VehicleStatus;
  purchaseDate: string;
  mileage: number;
  obdSerialNumber?: string;
  obdStatus: ObdStatus;
  id: string;
  soh: number;
  batteryScore: number;
  delta7d: number;
  lastSeen: string;
  fleetOpsStatus: FleetOpsStatus;
  series30d: number[];
  scoreRadar: number[];
}

// 운전자 데이터 인터페이스 확장
export interface Driver {
  id: string;
  name: string;
  depot: Depot | "";
  assignedVehicle?: string;
  contact: string;
  email: string;
  licenseType: LicenseType | "";
  isEmployed: boolean;
  businessRegistrationNumber?: string;
}

// --- 상수 정의 ---
export const vehicleModels: VehicleModel[] = ["현대 포터II 일렉트릭", "기아 봉고III EV"];
export const depots: Depot[] = ["동부산", "인천", "마포", "서울", "부산", "대구"];
export const statuses: VehicleStatus[] = ["운행중", "충전중", "수리 입고", "대기"];
export const licenseTypes: LicenseType[] = ["1종 보통", "1종 대형", "1종 특수 (대형견인)", "1종 특수 (소형견인)", "특수 (구난차)", "2종 보통", "2종 소형"];
export const assignees: string[] = [
    "김민준", "이서준", "박도윤", "최은우", "정시우", "강지호", "윤준서", "임하준", "한이안", "신유준",
    "박준호", "김건우", "최현준", "이민성", "정재현", "송지훈", "오세현", "유찬영", "황인우", "백승민"
];

// --- 데이터 생성 로직 (순서 보장) ---

// 1. 20명의 운전자 기본 정보 생성
const driverBaseData = assignees.map((name, i) => ({
    name: name,
    depot: depots[i % depots.length] as Depot,
    contact: `010-${1234 + i}-${5678 + i}`,
    email: "ilyang@ilyang.com",
    licenseType: i < 15 ? "1종 보통" : licenseTypes[i % licenseTypes.length],
    isEmployed: i % 3 !== 0,
    businessRegistrationNumber: i % 3 === 0 ? `123-45-${67890 + i}`: undefined,
}));

// 2. 차량을 할당받을 18명의 운전자 그룹 정의
const assignableDrivers = driverBaseData.slice(0, 18);

// 3. 차량 데이터 생성 (운전자 정보 사용)
const INITIAL_VEHICLES: Vehicle[] = Array.from({ length: 25 }, (_, i) => {
    const vehicleNumber = `12가 ${1111 + i}`;
    const soh = 80 + Math.random() * 20;
    const driverInfo = assignableDrivers[i % assignableDrivers.length];
    return {
        vehicleNumber,
        vin: `V${String(100000 + i * 7).padStart(6, '0')}`,
        model: i % 2 === 0 ? "기아 봉고III EV" : "현대 포터II 일렉트릭",
        assignee: driverInfo.name,
        depot: driverInfo.depot,
        status: statuses[i % statuses.length],
        purchaseDate: `${2022 + Math.floor(i / 10)}-${String((i % 12) + 1).padStart(2, '0')}-${String((i % 28) + 1).padStart(2, '0')}`,
        mileage: 10000 + Math.floor(Math.random() * 150000),
        obdSerialNumber: `E${String(1000 + i).padStart(4, '0')}`,
        obdStatus: i < 4 ? '에러' : '통신중',
        id: vehicleNumber,
        soh,
        batteryScore: Math.max(20, Math.min(100, Math.round(30 + ((soh - 80) / 20) * 60 + (Math.random() - 0.2) * 20))),
        delta7d: Math.round(((Math.random() - 0.5) * 5) * 10) / 10,
        lastSeen: new Date(Date.now() - Math.random() * 36e5 * 24 * 3).toISOString(),
        fleetOpsStatus: ["Open", "In progress", "Resolved"][i % 3] as FleetOpsStatus,
        series30d: Array.from({ length: 30 }, () => Math.max(80, Math.min(100, soh + (Math.random() - 0.5) * 4))),
        scoreRadar: Array.from({ length: 5 }, () => Math.max(20, Math.min(100, Math.round(70 + (Math.random() - 0.5) * 30)))),
    };
});

// 4. 최종 운전자 데이터 생성 (차량 정보 사용)
const INITIAL_DRIVERS: Driver[] = driverBaseData.map((driver, i) => {
    const assignedVehicle = INITIAL_VEHICLES.find(v => v.assignee === driver.name);
    return {
        id: `DRV-${String(101 + i).padStart(3, '0')}`,
        name: driver.name,
        depot: driver.depot,
        assignedVehicle: assignedVehicle?.vehicleNumber,
        contact: driver.contact,
        email: driver.email,
        licenseType: driver.licenseType,
        isEmployed: driver.isEmployed,
        businessRegistrationNumber: driver.businessRegistrationNumber,
    };
});


// --- DB 시뮬레이션 (In-memory Store) ---
let vehiclesDB: Vehicle[] = JSON.parse(JSON.stringify(INITIAL_VEHICLES));
let driversDB: Driver[] = JSON.parse(JSON.stringify(INITIAL_DRIVERS));
const SIMULATED_DELAY = 300;

// --- 데이터 접근 함수 (API 역할) ---

export const db = {
  fetchVehicles: (): Promise<Vehicle[]> => new Promise(resolve => setTimeout(() => resolve(JSON.parse(JSON.stringify(vehiclesDB))), SIMULATED_DELAY)),
  fetchDrivers: (): Promise<Driver[]> => new Promise(resolve => setTimeout(() => resolve(JSON.parse(JSON.stringify(driversDB))), SIMULATED_DELAY)),

  addVehicle: (newVehicle: Vehicle): Promise<Vehicle> => new Promise((resolve, reject) => {
    setTimeout(() => {
      if (vehiclesDB.some(v => v.vehicleNumber === newVehicle.vehicleNumber)) return reject(new Error("이미 등록된 차량번호입니다."));
      if (vehiclesDB.some(v => v.vin === newVehicle.vin)) return reject(new Error("이미 등록된 VIN입니다."));
      
      if (newVehicle.assignee) {
        const driverIndex = driversDB.findIndex(d => d.name === newVehicle.assignee);
        if (driverIndex !== -1) {
          driversDB[driverIndex].assignedVehicle = newVehicle.vehicleNumber;
        }
      }

      vehiclesDB = [newVehicle, ...vehiclesDB];
      resolve(newVehicle);
    }, SIMULATED_DELAY);
  }),

  updateVehicle: (updatedVehicle: Vehicle): Promise<Vehicle> => new Promise((resolve, reject) => {
    setTimeout(() => {
      const index = vehiclesDB.findIndex(v => v.vin === updatedVehicle.vin);
      if (index === -1) return reject(new Error("차량을 찾을 수 없습니다."));
      
      const oldVehicle = vehiclesDB[index];

      if (oldVehicle.assignee !== updatedVehicle.assignee) {
        if (oldVehicle.assignee) {
          const oldDriverIndex = driversDB.findIndex(d => d.name === oldVehicle.assignee);
          if (oldDriverIndex !== -1) {
            driversDB[oldDriverIndex].assignedVehicle = undefined;
          }
        }
        if (updatedVehicle.assignee) {
          const newDriverIndex = driversDB.findIndex(d => d.name === updatedVehicle.assignee);
          if (newDriverIndex !== -1) {
            driversDB[newDriverIndex].assignedVehicle = updatedVehicle.vehicleNumber;
          }
        }
      }

      vehiclesDB[index] = updatedVehicle;
      resolve(updatedVehicle);
    }, SIMULATED_DELAY);
  }),

  deleteVehicle: (vin: string): Promise<void> => new Promise(resolve => {
    setTimeout(() => {
      const vehicleToDelete = vehiclesDB.find(v => v.vin === vin);
      if (vehicleToDelete && vehicleToDelete.assignee) {
        const driverIndex = driversDB.findIndex(d => d.name === vehicleToDelete.assignee);
        if (driverIndex !== -1) {
          driversDB[driverIndex].assignedVehicle = undefined;
        }
      }
      vehiclesDB = vehiclesDB.filter(v => v.vin !== vin);
      resolve();
    }, SIMULATED_DELAY);
  }),

  addDriver: (newDriver: Driver): Promise<Driver> => new Promise((resolve, reject) => {
    setTimeout(() => {
      if (driversDB.some(d => d.name === newDriver.name)) return reject(new Error("이미 등록된 운전자 이름입니다."));
      if (driversDB.some(d => d.contact === newDriver.contact)) return reject(new Error("이미 등록된 연락처입니다."));
      driversDB = [newDriver, ...driversDB];
      resolve(newDriver);
    }, SIMULATED_DELAY);
  }),

  updateDriver: (updatedDriver: Driver): Promise<Driver> => new Promise((resolve, reject) => {
    setTimeout(() => {
      const index = driversDB.findIndex(d => d.id === updatedDriver.id);
      if (index === -1) return reject(new Error("운전자를 찾을 수 없습니다."));
      if (driversDB.some(d => d.contact === updatedDriver.contact && d.id !== updatedDriver.id)) return reject(new Error("이미 다른 운전자가 사용 중인 연락처입니다."));
      
      const oldDriver = driversDB[index];

      if(oldDriver.assignedVehicle !== updatedDriver.assignedVehicle) {
        if(oldDriver.assignedVehicle) {
          const oldVehicleIndex = vehiclesDB.findIndex(v => v.vehicleNumber === oldDriver.assignedVehicle);
          if(oldVehicleIndex !== -1) {
            vehiclesDB[oldVehicleIndex].assignee = undefined;
          }
        }
        if(updatedDriver.assignedVehicle) {
          const newVehicleIndex = vehiclesDB.findIndex(v => v.vehicleNumber === updatedDriver.assignedVehicle);
          if(newVehicleIndex !== -1) {
            vehiclesDB[newVehicleIndex].assignee = updatedDriver.name;
          }
        }
      }

      driversDB[index] = updatedDriver;
      resolve(updatedDriver);
    }, SIMULATED_DELAY);
  }),

  deleteDriver: (driverId: string): Promise<void> => new Promise(resolve => {
    setTimeout(() => {
      const driverToDelete = driversDB.find(d => d.id === driverId);
      if (driverToDelete && driverToDelete.assignedVehicle) {
        const vehicleIndex = vehiclesDB.findIndex(v => v.vehicleNumber === driverToDelete.assignedVehicle);
        if (vehicleIndex !== -1) {
          vehiclesDB[vehicleIndex].assignee = undefined;
        }
      }
      driversDB = driversDB.filter(d => d.id !== driverId);
      resolve();
    }, SIMULATED_DELAY);
  })
};
